import { useEffect, useRef, useState } from "react";
import { Minus, Plus, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const MAX_TEXT = 500;
const BULLET = "• ";
const stripBullet = (l) => l.replace(/^\s*[•\-*]\s*/, "").trim();

export const toBulletText = (v) => (Array.isArray(v) ? v.map((x) => BULLET + stripBullet(x)).join("\n") : v || "");
export const fromBulletText = (v) =>
  (typeof v === "string" ? v.split("\n").map(stripBullet).filter(Boolean) : (v || []).map(stripBullet).filter(Boolean));

const Counter = ({ len, testId }) => (
  <p className={`text-xs mt-1 text-right ${len >= MAX_TEXT ? "text-destructive font-medium" : "text-muted-foreground"}`} data-testid={testId}>
    Caracteres: {len} / {MAX_TEXT}
  </p>
);

export const MAX_WORDS = 300;
export const countWords = (t) => ((t || "").match(/\S+/g) || []).length;
const capWords = (t, max) => {
  const re = /\S+/g;
  let m, n = 0;
  while ((m = re.exec(t))) { n += 1; if (n === max) return t.slice(0, m.index + m[0].length); }
  return t;
};

export const WordCountTextarea = ({ label, value, onChange, testId, placeholder }) => {
  const [capped, setCapped] = useState(false);
  const n = countWords(value);
  const ok = true;
  const msg = (capped || n >= MAX_WORDS) ? `Llegaste al máximo de ${MAX_WORDS} palabras` : `Máximo ${MAX_WORDS} palabras`;
  return (
    <div>
      <Label>{label}</Label>
      <Textarea rows={8} value={value} placeholder={placeholder} data-testid={testId} className="leading-relaxed"
        onChange={(e) => {
          const t = e.target.value;
          if (countWords(t) > MAX_WORDS) { setCapped(true); onChange(capWords(t, MAX_WORDS)); } else { setCapped(false); onChange(t); }
        }} />
      <div className="h-1 rounded-full bg-muted mt-2 overflow-hidden">
        <div className={`h-full transition-[width,background-color] duration-300 ${ok ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${Math.min(100, (n / MAX_WORDS) * 100)}%` }} />
      </div>
      <div className="flex items-center justify-between gap-2 text-xs mt-1">
        <span className={!ok ? "text-muted-foreground" : n >= MAX_WORDS ? "text-amber-600 font-medium" : "text-emerald-600 font-medium"} data-testid={`${testId}-hint`}>{msg}</span>
        <span className="tabular-nums text-muted-foreground shrink-0" data-testid={`${testId}-counter`}>{n} / {MAX_WORDS} palabras</span>
      </div>
    </div>
  );
};

export const UpperNameField = ({ value, onChange, testId, max = 150 }) => (
  <div>
    <Label>Nombre del Taller</Label>
    <Input value={value} className="uppercase" data-testid={testId} placeholder="TALLER DE..." maxLength={max}
      onChange={(e) => onChange(e.target.value.toUpperCase().slice(0, max))} />
    <p className="text-xs text-muted-foreground mt-1">Se escribe automáticamente en mayúsculas.</p>
  </div>
);

export const CountedTextarea = ({ label, value, onChange, testId }) => (
  <div>
    <Label>{label}</Label>
    <Textarea value={value || ""} maxLength={MAX_TEXT} data-testid={testId}
      onChange={(e) => onChange(e.target.value.slice(0, MAX_TEXT))} />
    <Counter len={(value || "").length} testId={`${testId}-counter`} />
  </div>
);

export const BulletTextarea = ({ label, value, onChange, testId }) => {
  const ref = useRef(null);
  const text = toBulletText(value);
  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (text.length + 1 + BULLET.length > MAX_TEXT) return;
    const { selectionStart: s, selectionEnd: en } = e.target;
    const next = text.slice(0, s) + "\n" + BULLET + text.slice(en);
    onChange(next);
    const pos = s + 1 + BULLET.length;
    requestAnimationFrame(() => ref.current?.setSelectionRange(pos, pos));
  };
  const normalize = () => {
    const lines = text.split("\n").map(stripBullet).filter(Boolean);
    onChange(lines.map((l) => BULLET + l).join("\n").slice(0, MAX_TEXT));
  };
  return (
    <div>
      <Label>{label}</Label>
      <Textarea ref={ref} rows={4} value={text} maxLength={MAX_TEXT} data-testid={testId} placeholder="• Materiales incluidos"
        onFocus={() => { if (!text) onChange(BULLET); }} onBlur={normalize} onKeyDown={onKeyDown}
        onChange={(e) => onChange(e.target.value.slice(0, MAX_TEXT))} />
      <Counter len={text.length} testId={`${testId}-counter`} />
    </div>
  );
};

const STEP = 15, MIN_D = 15, MAX_D = 480;
const asHours = (m) => {
  const h = Math.floor(m / 60), r = m % 60;
  if (!h) return `${r} min`;
  const hs = `${h} ${h === 1 ? "hr." : "hrs."}`;
  return r ? `${hs} ${r} min` : hs;
};

const StepButton = ({ onStep, disabled, label, testId, children }) => {
  const timer = useRef(null);
  const stop = () => { clearTimeout(timer.current); clearInterval(timer.current); timer.current = null; };
  useEffect(() => stop, []);
  const start = (e) => {
    e.preventDefault();
    onStep();
    timer.current = setTimeout(() => { timer.current = setInterval(onStep, 120); }, 400);
  };
  return (
    <button type="button" aria-label={label} disabled={disabled} data-testid={testId}
      onPointerDown={start} onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onStep(); } }}
      className="h-11 w-11 shrink-0 rounded-full border border-primary/30 bg-primary/10 text-primary flex items-center justify-center hover:bg-primary hover:text-primary-foreground active:scale-95 transition-[background-color,color,transform] disabled:opacity-40 disabled:pointer-events-none">
      {children}
    </button>
  );
};

export const DurationField = ({ value, onChange, testId }) => {
  const n = Number(value) || 60;
  const valRef = useRef(n);
  valRef.current = n;
  const step = (d) => {
    const cur = valRef.current;
    const next = Math.min(MAX_D, Math.max(MIN_D, d > 0 ? Math.floor(cur / STEP) * STEP + STEP : Math.ceil(cur / STEP) * STEP - STEP));
    valRef.current = next;
    onChange(next);
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label>Duración por cada sesión (minutos)</Label>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold" data-testid={`${testId}-hours`}>
          <Clock className="w-3.5 h-3.5" />{asHours(n)}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <StepButton onStep={() => step(-1)} disabled={n <= MIN_D} label="Restar 15 minutos" testId={`${testId}-minus`}><Minus className="w-5 h-5" /></StepButton>
        <div className="h-11 w-20 rounded-md border border-input bg-muted/40 grid place-items-center text-lg font-bold tabular-nums select-none"
          role="status" aria-live="polite" data-testid={testId}>{n}</div>
        <StepButton onStep={() => step(1)} disabled={n >= MAX_D} label="Sumar 15 minutos" testId={`${testId}-plus`}><Plus className="w-5 h-5" /></StepButton>
      </div>
      <p className="text-xs text-muted-foreground mt-2">Define la duración de cada sesión de este taller. Usa − y + para cambiarla de 15 en 15 minutos.</p>
    </div>
  );
};
